'use client';

import { useEffect, useMemo } from 'react';

export type Option = { value: string | number };
/**
 * Warn when the value is in a list of options.
 *
 * @example
 * const options = [{value: 'a', name: 'A'}, {value: 'b', name: 'Bee'}];
 * const value = 'c';
 * useAssertValue(value, options); // Will log a warning to the console
 */
export function useAssertValue(
  value: string | number | undefined,
  options: Option[],
): void {
  const warningMessage = useMemo<null | string>(() => {
    if (value === undefined) {
      return null;
    }

    if (options.some((o) => o.value === value)) {
      return null;
    }

    return `Value ${value} is not in the options`;
  }, [value, options]);

  // This way we only log the warning once instead of repeatedly
  useEffect(() => {
    if (warningMessage) {
      console.warn(warningMessage);
    }
  }, [warningMessage]);
}
